import { useCallback, useRef, useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button, Space, Select, Tag, Modal, message } from 'antd';
import { PlusOutlined, EyeOutlined, EditOutlined, DeleteOutlined } from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import { useTranslation } from 'react-i18next';
import { DataGrid, type DataGridHandle } from '@/components/DataGrid';
import type { SearchParams } from '@/api/types';
import {
  getReportDefinitions, deleteReportDefinition,
  type ReportDefinition, type ReportDefinitionFilters,
} from '@/api/dashboardApi';
import { useAuth } from '@/hooks/useAuth';

const CATEGORIES = ['sales', 'customer', 'activity', 'pipeline'] as const;
const FORMATS = ['table', 'chart', 'csv'] as const;

export default function ReportListPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { hasPermission } = useAuth();
  const gridRef = useRef<DataGridHandle>(null);

  const [filters, setFilters] = useState<ReportDefinitionFilters>({});

  const canWrite = hasPermission('reports', 'write');
  const canDelete = hasPermission('reports', 'delete');

  const handleFilterChange = useCallback(
    (key: keyof ReportDefinitionFilters, value: string | undefined) => {
      setFilters((prev) => ({ ...prev, [key]: value }));
    },
    []
  );

  const fetchData = useCallback(
    (params: SearchParams) => getReportDefinitions({ ...params, ...filters }),
    [filters]
  );

  const filterMounted = useRef(false);
  useEffect(() => {
    if (!filterMounted.current) {
      filterMounted.current = true;
      return;
    }
    gridRef.current?.reload();
  }, [filters]);

  const handleDelete = useCallback(
    (id: string) => {
      Modal.confirm({
        title: t('app.confirm'),
        content: t('reports.confirm_delete'),
        okText: t('app.yes'),
        cancelText: t('app.no'),
        okType: 'danger',
        onOk: async () => {
          try {
            await deleteReportDefinition(id);
            message.success(t('reports.delete_success'));
            gridRef.current?.reload();
          } catch (err) {
            message.error(err instanceof Error ? err.message : t('app.error'));
          }
        },
      });
    },
    [t]
  );

  const columns: ColumnsType<ReportDefinition> = [
    {
      title: t('reports.field.name'),
      dataIndex: 'name',
      key: 'name',
      ellipsis: true,
    },
    {
      title: t('reports.field.description'),
      dataIndex: 'description',
      key: 'description',
      ellipsis: true,
      render: (val: string) => val || '—',
    },
    {
      title: t('reports.field.category'),
      dataIndex: 'category',
      key: 'category',
      width: 120,
      render: (val: string) => (
        <Tag>{val ? t(`reports.category.${val}`) : '—'}</Tag>
      ),
    },
    {
      title: t('reports.field.output_format'),
      dataIndex: 'output_format',
      key: 'output_format',
      width: 100,
      render: (val: string) => (
        <Tag color="blue">{val ? t(`reports.format.${val}`) : '—'}</Tag>
      ),
    },
    {
      title: t('reports.field.parameters'),
      dataIndex: 'parameters',
      key: 'parameters',
      width: 90,
      align: 'center',
      render: (val: unknown[]) => val?.length ?? 0,
    },
    {
      title: t('reports.field.owner'),
      dataIndex: 'owner_id',
      key: 'owner_id',
      width: 140,
      ellipsis: true,
    },
    {
      title: t('app.actions'),
      key: 'actions',
      width: 140,
      fixed: 'right',
      render: (_: unknown, record: ReportDefinition) => (
        <Space size="small">
          <Button
            type="link"
            size="small"
            icon={<EyeOutlined />}
            onClick={() => navigate(`/reports/${record.id}`)}
          />
          {canWrite && (
            <Button
              type="link"
              size="small"
              icon={<EditOutlined />}
              onClick={() => navigate(`/reports/${record.id}/edit`)}
            />
          )}
          {canDelete && (
            <Button
              type="link"
              size="small"
              danger
              icon={<DeleteOutlined />}
              onClick={() => handleDelete(record.id)}
            />
          )}
        </Space>
      ),
    },
  ];

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 16 }}>
        <Space>
          <Select
            style={{ width: 160 }}
            placeholder={t('reports.field.category')}
            allowClear
            value={filters.category}
            onChange={(val) => handleFilterChange('category', val)}
            options={CATEGORIES.map((c) => ({
              value: c,
              label: t(`reports.category.${c}`),
            }))}
          />
          <Select
            style={{ width: 140 }}
            placeholder={t('reports.field.output_format')}
            allowClear
            value={filters.output_format}
            onChange={(val) => handleFilterChange('output_format', val)}
            options={FORMATS.map((f) => ({
              value: f,
              label: t(`reports.format.${f}`),
            }))}
          />
        </Space>
        {canWrite && (
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={() => navigate('/reports/new')}
          >
            {t('reports.new_report')}
          </Button>
        )}
      </div>
      <DataGrid<ReportDefinition>
        ref={gridRef}
        columns={columns}
        fetchData={fetchData}
        rowKey="id"
        searchPlaceholder={t('reports.search_placeholder')}
      />
    </div>
  );
}
